import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PhasedReviewDialog,
  type PhasedReviewDialogPhase,
} from "@/components/ui/VersionReviewDialog/PhasedReviewDialog";

interface SaveToRegistryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<(() => void) | void> | (() => void) | void;
}

const SUCCESS_TRANSITION_DELAY_MS = 900;

export const SaveToRegistryDialog = ({ isOpen, onClose, onSave }: SaveToRegistryDialogProps) => {
  const [name, setName] = useState("");
  const [phase, setPhase] = useState<PhasedReviewDialogPhase>("review");
  const isLoading = phase === "saving";

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setPhase("review");
    }
  }, [isOpen]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || isLoading) {
      return;
    }

    setPhase("saving");

    try {
      const nextSuccessAction = await onSave(trimmedName);
      setPhase("success");

      await new Promise(resolve => {
        setTimeout(resolve, SUCCESS_TRANSITION_DELAY_MS);
      });

      nextSuccessAction?.();
      onClose();
    } catch {
      setPhase("review");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && name.trim() && !isLoading) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <PhasedReviewDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Save to Registry"
      phase={phase}
      reviewContentStyle={{ width: "425px", maxWidth: "92vw" }}
      statusTitle={phase === "saving" ? "Saving prompt template" : "Prompt template saved"}
      statusDescription={
        phase === "saving"
          ? "Saving this inline prompt template to the registry."
          : "Your prompt template is ready to reuse from the registry."
      }
      reviewContent={
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Save this inline prompt template to the registry for reuse across your workspace.
          </p>
          <div className="grid gap-2 py-2">
            <Label htmlFor="prompt-name">Prompt Name</Label>
            <Input
              id="prompt-name"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter a name for your prompt"
              disabled={isLoading}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={!name.trim() || isLoading}>
              Save
            </Button>
          </DialogFooter>
        </div>
      }
    />
  );
};
